import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import {
  AuthService,
  UserAccess,
  UserRoles,
} from '../../../services/auth.service';
import { UserService } from '../../../services/user.service';
import {
  FeatureSet,
  FeatureSetService,
} from '../../../services/feature-set.service';

@Component({
  selector: 'app-invite-user-dialog',
  templateUrl: './invite-user-dialog.component.html',
  styleUrl: './invite-user-dialog.component.scss',
})
export class InviteUserDialogComponent implements OnInit {
  form: FormGroup;
  featureSets: FeatureSet[] = [];
  showFeatureSetField = false;

  constructor(
    protected dialogRef: NbDialogRef<InviteUserDialogComponent>,
    protected authService: AuthService,
    protected userService: UserService,
    protected featureSetService: FeatureSetService,
    protected fb: FormBuilder,
    protected nbToastrService: NbToastrService
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      role: ['', Validators.required],
      feature_set_id: [null],
    });
  }

  ngOnInit(): void {
    // Always load feature sets — show dropdown when any exist
    this.featureSetService.loadFeatureSets().subscribe((sets) => {
      this.featureSets = sets;
      this.showFeatureSetField = sets.length > 0;
    });
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload: any = {
      name: this.form.value.name,
      email: this.form.value.email,
      role: this.form.value.role,
    };
    if (this.form.value.feature_set_id) {
      payload.feature_set_id = this.form.value.feature_set_id;
    }

    await this.userService.inviteUser(payload).subscribe(() => {
      setTimeout(() => {
        this.nbToastrService.success(
          'Der neu hinzugefügte Benutzer sollte nun eine Einladungs E-Mail erhalten haben.',
          'Benutzer erfolgreich eingeladen!'
        );
        this.dialogRef.close();
      }, 300);
    });
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }

  getFeatureSetDisplayName(featureSet: FeatureSet) {
    return this.featureSetService.getDisplayName(featureSet);
  }

  // Tooltip texts exist only for these roles (input.role.tooltips.* in i18n)
  private readonly rolesWithTooltip: string[] = [
    UserRoles.Admin,
    UserRoles.Manager,
    UserRoles.User,
  ];

  hasRoleTooltip(role: string): boolean {
    return this.rolesWithTooltip.includes(role);
  }

  get roles() {
    if (!this.authService.hasAccess(UserAccess.UserDialogSuperAdminRole)) {
      return Object.values(UserRoles).filter(
        (role) => role !== UserRoles.SuperAdmin
      );
    }

    return Object.values(UserRoles);
  }

  /** i18n key describing what a role may do, generically — the access model,
   *  not a feature list, so it stays correct as features are added. */
  roleHintKey(role: string | null | undefined): string {
    return role ? `dialogs.inviteUser.roleInfo.${role}` : '';
  }

  get selectedRole(): string {
    return this.form.get('role')?.value || '';
  }
}
